import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Upload, CheckCircle, X, Building2, Loader2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EditBusiness() {
  const { token } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [business, setBusiness] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    phone: "",
    address: "",
    website: "",
    logo_base64: null,
  });
  
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API}/businesses/edit/${token}`);
      setBusiness(res.data);
      setFormData({
        name: res.data.name || "",
        description: res.data.description || "",
        phone: res.data.phone || "",
        address: res.data.address || "",
        website: res.data.website || "",
        logo_base64: res.data.logo_base64 || null,
      });
      setLogoPreview(res.data.logo_base64);
    } catch (e) {
      setError(e.response?.data?.detail || "Lien invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      setFormData(prev => ({ ...prev, logo_base64: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, logo_base64: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Le nom est requis");
      return;
    }

    setSaving(true);
    
    try {
      await axios.put(`${API}/businesses/edit/${token}`, formData);
      toast.success("Fiche mise à jour avec succès!");
      navigate(`/entreprise/${business.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-lagoon animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center px-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card rounded-3xl p-8 md:p-12 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 rounded-full bg-coral/10 flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-coral" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-lagoon-900 mb-4">
            Lien invalide
          </h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <Link to="/">
            <Button className="btn-primary">Retour à l'accueil</Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-lagoon-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="home-logo">
            <div className="w-10 h-10 rounded-full bg-lagoon flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-xl font-semibold text-lagoon-900">Annuaire Polynésien</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-28 pb-16 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-10">
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-lagoon-900 mb-4">
                Modifier ma fiche
              </h1>
              <p className="text-gray-600">
                Mettez à jour les informations de votre entreprise
              </p>
            </div>

            <form onSubmit={handleSubmit} className="glass-card rounded-3xl p-8 md:p-10" data-testid="edit-form">
              {/* Logo Upload */}
              <div className="mb-8">
                <Label className="text-lagoon-900 font-semibold mb-3 block">Logo de l'entreprise</Label>
                <div className="flex items-center gap-6">
                  {logoPreview ? (
                    <div className="relative">
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="w-24 h-24 rounded-xl object-cover"
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-coral rounded-full flex items-center justify-center text-white hover:bg-coral-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-xl bg-lagoon-50 flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-lagoon" />
                    </div>
                  )}
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label 
                      htmlFor="logo-upload"
                      className="btn-primary inline-flex items-center gap-2 cursor-pointer text-sm"
                    >
                      <Upload className="w-4 h-4" />
                      {logoPreview ? 'Changer' : 'Télécharger'}
                    </label>
                    <p className="text-xs text-gray-500 mt-2">JPG, PNG ou GIF. Max 5MB.</p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <Label htmlFor="name" className="text-lagoon-900 font-semibold">
                    Nom de l'entreprise <span className="text-coral">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-2 h-12 bg-white/50 border-lagoon-100 rounded-xl"
                    data-testid="name-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description" className="text-lagoon-900 font-semibold">
                    Description
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="mt-2 bg-white/50 border-lagoon-100 rounded-xl min-h-[120px]"
                    data-testid="description-input"
                  />
                </div>

                {/* Phone & Address */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-lagoon-900 font-semibold">
                      Téléphone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="mt-2 h-12 bg-white/50 border-lagoon-100 rounded-xl"
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address" className="text-lagoon-900 font-semibold">
                      Adresse
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="mt-2 h-12 bg-white/50 border-lagoon-100 rounded-xl"
                      data-testid="address-input"
                    />
                  </div>
                </div>

                {/* Website */}
                <div>
                  <Label htmlFor="website" className="text-lagoon-900 font-semibold">
                    Site web
                  </Label>
                  <Input
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="mt-2 h-12 bg-white/50 border-lagoon-100 rounded-xl"
                    placeholder="www.entreprise.pf"
                    data-testid="website-input"
                  />
                </div>

                {/* Email (read-only) */}
                <div>
                  <Label className="text-lagoon-900 font-semibold">Email</Label>
                  <p className="mt-2 text-gray-500 text-sm">
                    {business?.email} (non modifiable)
                  </p>
                </div>
              </div>

              {/* Submit */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Button 
                  type="submit" 
                  className="btn-coral flex-1 h-14 text-base"
                  disabled={saving}
                  data-testid="save-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-2" />
                      Enregistrer les modifications
                    </>
                  )}
                </Button>
                <Link to={business ? `/entreprise/${business.id}` : '/'} className="flex-shrink-0">
                  <Button 
                    type="button"
                    variant="outline"
                    className="h-14 px-8 border-lagoon text-lagoon hover:bg-lagoon-50 w-full"
                  >
                    Annuler
                  </Button>
                </Link>
              </div>
            </form>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
